'use client';

/**
 * ProjectActions - Project-level quick actions
 *
 * Available actions:
 * - Create new RFQ
 * - View project timeline
 * - Export project summary
 * - Open in Odoo
 *
 * @component_role Project-level Actions
 * @business_rule Quick actions for project context: Create new RFQ (pre-fills project),
 *   View project timeline, Export project summary. "Open in Odoo" deep-links to
 *   Odoo's project form for full editing.
 * @user_outcome Start procurement workflows from the project view without navigating
 *   menus. One click to create RFQ with project already set.
 * @integration_point BFF data gateway → project_detail strategy, Odoo deep links
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  FileText,
  Calendar,
  Download,
  ExternalLink,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface ProjectActionsProps {
  projectId: string;
}

export function ProjectActions({ projectId }: ProjectActionsProps) {
  const detailStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'detail'));
  const { data } = useStrategyData(detailStrategy ?? 'project_detail', { semantic_id: projectId });
  const project = data?.data as Record<string, unknown> | undefined;

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  const actions = [
    {
      label: 'Create New RFQ',
      description: 'Start a new request for quote',
      icon: Plus,
      onClick: () => {
        toast.info('Create RFQ', {
          description: 'RFQ creation wizard coming soon',
        });
      },
      primary: true,
    },
    {
      label: 'View Timeline',
      description: 'See project activity timeline',
      icon: Calendar,
      onClick: () => {
        toast.info('Timeline', {
          description: 'Timeline view coming soon',
        });
      },
    },
    {
      label: 'Export Summary',
      description: 'Download project report',
      icon: Download,
      onClick: () => {
        toast.info('Export', {
          description: 'Export functionality coming soon',
        });
      },
    },
    {
      label: 'Open in Odoo',
      description: 'View full project details',
      icon: ExternalLink,
      onClick: () => {
        if (project?.odoo_id) {
          window.open(`${ODOO_HOST}/web#id=${project.odoo_id}&model=project.project&view_type=form`, '_blank');
        } else {
          toast.error('Cannot open', { description: 'Project ID not available' });
        }
      },
      disabled: !project?.odoo_id,
    },
  ];

  return (
    <div className="space-y-4 p-3">
      {/* Header */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">PROJECT ACTIONS</h4>
        {project && (
          <div className="p-2 rounded-lg bg-card border border-border">
            <div className="text-sm font-medium truncate">{String(project.name)}</div>
          </div>
        )}
      </div>

      {/* Actions List */}
      <div className="space-y-2">
        {actions.map((action, i) => (
          <button
            key={i}
            onClick={action.onClick}
            disabled={action.disabled}
            className={`
              flex items-center justify-between w-full p-3 rounded-lg
              transition-colors text-left
              ${action.primary
                ? 'bg-primary/10 hover:bg-primary/20 border border-primary/20'
                : 'hover:bg-accent border border-transparent'
              }
              ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-md ${action.primary ? 'bg-primary/20' : 'bg-muted'}`}>
                <action.icon className={`h-4 w-4 ${action.primary ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <div>
                <div className="text-sm font-medium">{action.label}</div>
                <div className="text-xs text-muted-foreground">{action.description}</div>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </button>
        ))}
      </div>

      {/* Footer */}
      <div className="text-xs text-muted-foreground pt-2 border-t border-border">
        Select an RFQ or product for more specific actions
      </div>
    </div>
  );
}
